import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    Button,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import { Flag, CheckCircle, Cancel, Replay } from '@mui/icons-material';

const STATUS_COLORS = {
    open: 'warning',
    resolved: 'success',
    dismissed: 'default',
};

function formatDate(value) {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

export default function ReportsTab({
    reports = [],
    summary = { total: 0, open: 0 },
    filters = { status: '' },
    onApplyFilters,
    onUpdateStatus,
}) {
    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Reports
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        User-submitted reports. Each report is a free-text description of what happened.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label={`${summary.open ?? 0} open`} color="warning" />
                    <Chip label={`${summary.total ?? 0} total`} variant="outlined" />
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            label="Status"
                            value={filters.status || ''}
                            onChange={(e) => onApplyFilters?.({ status: e.target.value })}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="open">Open</MenuItem>
                            <MenuItem value="resolved">Resolved</MenuItem>
                            <MenuItem value="dismissed">Dismissed</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
            </Box>

            {reports.length === 0 ? (
                <Card sx={{ p: 6, textAlign: 'center', borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)', border: '1px solid rgba(0, 0, 0, 0.05)' }}>
                    <Flag sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No reports
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Reports submitted by users will appear here.
                    </Typography>
                </Card>
            ) : (
                <Stack spacing={2}>
                    {reports.map((report) => (
                        <Card
                            key={report._id}
                            sx={{
                                borderRadius: 3,
                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                                border: '1px solid rgba(0, 0, 0, 0.05)',
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                                    <Box sx={{ flex: 1, minWidth: 240 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {report.placeName || 'Unknown place'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDate(report.createdAt)}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        label={report.status}
                                        color={STATUS_COLORS[report.status] || 'default'}
                                        size="small"
                                        sx={{ textTransform: 'capitalize' }}
                                    />
                                </Box>

                                <Box
                                    sx={{
                                        mt: 2,
                                        p: 2,
                                        borderRadius: 2,
                                        bgcolor: 'action.hover',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {report.note || '(no details provided)'}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            Reporter: {report.reporterName || 'Unknown'}{report.reporterEmail ? ` (${report.reporterEmail})` : ''}
                                        </Typography>
                                        {report.reason && report.reason !== 'User report' && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                Source: {report.reason}
                                            </Typography>
                                        )}
                                    </Box>
                                    {report.status === 'open' ? (
                                        <Stack direction="row" spacing={1}>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="success"
                                                startIcon={<CheckCircle />}
                                                onClick={() => onUpdateStatus?.(report._id, 'resolved')}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                Resolve
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="inherit"
                                                startIcon={<Cancel />}
                                                onClick={() => onUpdateStatus?.(report._id, 'dismissed')}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                Dismiss
                                            </Button>
                                        </Stack>
                                    ) : (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<Replay />}
                                            onClick={() => onUpdateStatus?.(report._id, 'open')}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Reopen
                                        </Button>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
            )}
        </Box>
    );
}